/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard
 * (C) Copyright IBM Corp. 2020, 2022
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'../../../app/nls/StringResources',
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'../../../lib/@waca/dashboard-common/dist/api/PropertiesProviderAPI',
	'dashboard-core/js/dashboard/widgets/PropertyListUtil',
	'underscore'
], (StringResources, APIFactory, PropertiesProviderAPI, PropertyListUtil, _) => {
	const TAB_POSITION = [
		{
			'value': 'top',
			'label': StringResources.get('propTop')
		},
		{
			'value': 'left',
			'label': StringResources.get('propLeft')
		},
		{
			'value': 'bottom',
			'label': StringResources.get('propBottom')
		},
		{
			'value': 'right',
			'label': StringResources.get('propRight')
		}
	];

	const TAB_ICON_POSITION = [
		{
			'value': 'right',
			'label': StringResources.get('propRightTitle')
		},
		{
			'value': 'left',
			'label': StringResources.get('propLeftTitle')
		},
		{
			'value': 'top',
			'label': StringResources.get('propTopTitle')
		},
		{
			'value': 'bottom',
			'label': StringResources.get('propBottomTitle')
		}
	];

	class TabProperties {
		constructor({ dashboardApi, dashboardAPI, features, content, options }) {
			this._dashboard = dashboardApi || dashboardAPI;
			const featureChecker = this._dashboard && this._dashboard.getGlassCoreSvc('.FeatureChecker');
			this.isPropertiesV2Enabled = featureChecker && featureChecker.checkValue('dashboard', 'PropertiesV2', 'enabled');
			this._content = content;
			this._features = features;
			this._allowHidingMultipleTabs = !options || options.allowHidingMultipleTabs !== false;
			this._content.getFeature('Properties').registerProvider(this.getAPI());
		}

		getAPI() {
			return APIFactory.createAPI(this, [PropertiesProviderAPI]);
		}

		_getCanvas() {
			return this._features['Canvas'];
		}

		getLayoutModel(id = this._content.getId()) {
			const boardModel = this._features['internal'].getBoardModel();
			return boardModel.layout.findModel(id);
		}

		getPropertyList() {
			const propertyList = this._getTabPropertiesList();

			// Default the setPropertyValue and getPropertyValue for any property that doesn't define those methods
			propertyList.forEach((property) => {
				if (!property.setPropertyValue) {
					property.setPropertyValue = (value) => this._setPropertyValueInModel(property.id, value);
				}

				if (!property.getPropertyValue) {
					property.getPropertyValue = () => this._getPropertyValueFromModel(property.id);
				}
			});

			return propertyList;
		}


		_getInnerTabPropertyLayoutList() {
			const propertiesLayoutSpec = [{
				id: 'general',
				type: 'Group',
				label: StringResources.get('tabName_general')
			}, {
				'id': 'banner',
				'type': 'Banner',
				'value': 'Tab widget properties',
				'editable': false
			}, {
				id: 'tabs',
				type: 'Section',
				label: StringResources.get('sectionName_tabs'),
				position: 2
			}, {
				id: 'masterTabStyles',
				type: 'Section',
				collapsible: false,
				label: StringResources.get(this.isPropertiesV2Enabled ? 'propMasterTabStylesLabel' : 'propMasterTabStylesLabel_v1')
			}, {
				id: 'individualTabStyles',
				type: 'Section',
				collapsible: false,
				label: StringResources.get('propIndividualTabStylesLabel')
			}, {
				id: 'layout',
				type: 'Section',
				open: true,
				label: StringResources.get('sectionName_layout'),
				position: 3
			}, {
				id: 'widgetPosition',
				type: 'Section',
				collapsible: false,
				label: StringResources.get('propPositionLabel'),
				position: 2
			}, {
				id: 'moveWidgetSplit',
				type: 'Split',
				items: [{
					id: 'left',
					align: 'left',
					items: []
				}, {
					id: 'right',
					align: 'right',
					items: []
				}]
			}, {
				id: 'resizeWidget',
				type: 'Section',
				collapsible: false,
				label: StringResources.get('propResizeLabel'),
				position: 3
			}, {
				id: 'resizeWidgetSplit',
				type: 'Split',
				items: [{
					id: 'left',
					align: 'left',
					items: []
				}, {
					id: 'right',
					align: 'right',
					items: []
				}]
			}];
			if (this.isPropertiesV2Enabled) {
				// remove group as Carbon design no longer have Tab
				propertiesLayoutSpec.splice(0, 1);
			}
			return propertiesLayoutSpec;
		}

		getPropertyLayoutList() {
			if (this._isInnerTab()) {
				return this._getInnerTabPropertyLayoutList();
			}

			const propertiesLayoutSpec = [{
				id: 'general',
				type: 'Group',
				label: StringResources.get('tabName_general')
			}, {
				id: 'tabs',
				type: 'Section',
				label: StringResources.get('sectionName_tabs'),
				position: 3
			}, {
				id: 'masterTabStyles',
				type: 'Section',
				collapsible: false,
				label: StringResources.get(this.isPropertiesV2Enabled ? 'propMasterTabStylesLabel' : 'propMasterTabStylesLabel_v1')
			}, {
				id: 'individualTabStyles',
				type: 'Section',
				collapsible: false,
				label: StringResources.get('propIndividualTabStylesLabel')
			}];
			if (this.isPropertiesV2Enabled) {
				// remove group as Carbon design no longer have Tab
				propertiesLayoutSpec.splice(0, 1);
			}
			return propertiesLayoutSpec;
		}

		reset() { }

		_individualTabSlideOut(page) {
			// Waiting for the content is Ready in here will force the property provider content feature to be registered
			// on page that are not rendered yet (since we delay the features)
			// TODO - This a temporary hack for now to get it to work in 11.2,  but we need to clean this up and have a better story
			this._features['Canvas.internal'].whenContentReady(page.getId()).then(() => {
				const propertiesManager = this._dashboard.getDashboardCoreSvc('propertiesManager');
				return propertiesManager.getPropertyLayoutList([page]).then(properties => {
					return propertiesManager.addChild({
						overlay: true,
						label: StringResources.get('individualTabProperties'),
						content: {
							module: 'dashboard/contentpane/PropertyUIControlView',
							items: properties,
							content: page, // TODO: still used in properties v1 - remove when PropertiesV2 feature flag is enabled
							propertyList: page.getPropertyList(),
							propertyLayoutList: page.getPropertyLayoutList(),
							refreshChild: true
						},
						pageChildId: page.getId()
					});
				});
			});
		}

		_getIndividualTabStylesLinks(sectionIndividualTabStylesId) {
			let content;
			if (this._isInnerTab()) {
				content = this._content.findContent({ type: 'page' });
			} else {
				content = this._getCanvas().findContent({ type: 'page' })
					.filter(page => !this._isInnerPage(page));
			}

			return content.map((page, index) => {
				return this._getIndividualTabStylesLinksForPages(page, index, sectionIndividualTabStylesId);
			});
		}

		_isInnerPage(page) {
			let container = page;
			let isInnerPage = false;
			while (!isInnerPage && container) {
				container = container.getContainer();
				isInnerPage = container && container.getType() !== 'tab'; // inner tab container type is 'genericPage'
			}
			return isInnerPage;
		}

		_getIndividualTabStylesLinksForPages(page, index, sectionIndividualTabStylesId) {
			const clickCallback = () => {
				this._individualTabSlideOut(page);
			};
			const layoutModel = this.getLayoutModel(page.getId());
			let tabIcon = page.getPropertyValue('tabIcon');

			if (!tabIcon){
				// This case is hack for now. We are not loading the features for non visible tabs which cause the property
				// provider to to register the proper getPropertyValue.
				// TODO - The real fix is to move the property to proper place and stop using a cutom getPropertyValue
				const icon = layoutModel.get('tabIcon');
				if(icon && icon !== 'transparent') {
					tabIcon = this._features['Dashboard.Icons'].getIcon(icon).id;
				} else {
					tabIcon = 'transparent';
				}
			}


			// Defaulting to the layoutModel is hack for now. We are not loading the features for non visible tabs which cause the property
			// provider to to register the proper getPropertyValue.
			// TODO - The real fix is to move the property to proper place and stop using a cutom getPropertyValue
			const title = page.getPropertyValue('title') ||  layoutModel.get('title');

			return {
				id: 'tabIndividualStylingLink' + index,
				editor: {
					sectionId: sectionIndividualTabStylesId,
					uiControl: {
						type: 'SingleLineLinks',
						clickCallback: clickCallback,
						items: [{
							align: 'left',
							items: [{
								type: 'icon',
								svgIcon: tabIcon !== 'transparent' ? tabIcon : null,
								iconTooltip: StringResources.get('propIndividualTabStylesAriaLabel')
							}]
						}, {
							align: 'right',
							items: [{
								type: 'text',
								value: title,
								showRightArrow: true
							}]
						}]
					}
				}
			};
		}

		_isInnerTab() {
			return this._content.getType() === 'tab' && this._content.getContainer();
		}

		_updateStyleValueInModel(styleName, value) {
			let styleValue = value;
			const model = this.getLayoutModel();
			const newStyle = _.extend({}, model.style);
			newStyle[styleName] = styleValue;

			// Todo: we can stop using the topLayoutModel once the undo/redo uses
			// the API events, but for now using the model directly doesn't add to the stack
			// if the widget is not on the same tab that it was initally added on.
			model.getTopLayoutModel().updateModel({
				updateArray: [{
					id: model.id,
					style: newStyle
				}]
			}, this, {
				skipUndoRedo: true
			});

			return styleValue;
		}

		_onChangePercentOrPixelProperty(constrainedBy, propertyName, propertyValue, options) {
			const units = this.getUnit();
			let minimum =  25;
			if ( minimum && units === '%'){
				// get the minimum value to percent
				const domFeature = this._content.getContainer().getFeature('ContentViewDOM');
				if (domFeature){
					const node = domFeature.getNode();
					const size = propertyName === 'height' ?  node.clientHeight : node.clientWidth;
					minimum = 100 * minimum / size;
				} else {
					// without a dom we can't figure out the minimum percent.. so we use 0
					minimum = 0;
				}
			}

			let numericValue = this._getNumericValueWithConstraint(constrainedBy, propertyValue, minimum);

			let formattedValue = PropertyListUtil.getPropertyDisplayString(numericValue, units);

			this._content.setPropertyValue(propertyName, formattedValue, options);
		}

		getUnit() {
			return this._getCanvas().getPropertyValue('layoutPositioning') === 'absolute' ? 'px' : '%';
		}

		_getMaxValueForConstraint(constrainedBy) {
			if (this._getCanvas().getPropertyValue('layoutPositioning') === 'relative') {
				return 100;
			} else {
				if (constrainedBy === 'width' || constrainedBy === 'left') {
					return parseFloat(this._getCanvas().getPropertyValue('pageSizeWidth'));
				} else {
					return parseFloat(this._getCanvas().getPropertyValue('pageSizeHeight'));
				}
			}
		}

		_getNumericValueWithConstraint(constrainedBy, propertyValue, minimum) {
			let numericValue = parseFloat(propertyValue);
			if (isNaN(numericValue) ) {
				numericValue = 0;
			}
			let constraint = this.content.getPropertyValue(constrainedBy) || 0;
			const maxValue = this._getMaxValueForConstraint(constrainedBy);
			numericValue = Math.max(Math.min(maxValue, numericValue), minimum || 0);
			constraint = parseFloat(constraint);
			if (constraint + numericValue > maxValue) {
				numericValue = maxValue - constraint;
			}

			return numericValue;
		}

		_getInnerTabPropertiesList() {
			const restoreMasterStyling = () => {
				const transaction = this._features['Transaction'];
				const transactionToken = transaction.startTransaction();
				this._content.setPropertyValue('tabTextColor', 'transparent', transactionToken);
				this._content.setPropertyValue('tabSelectedLineColor', 'transparent', transactionToken);
				this._content.setPropertyValue('tabBackgroundColor', 'transparent', transactionToken);
				transaction.endTransaction(transactionToken);
				this._dashboard.triggerDashboardEvent('properties:refreshPane', {
					focusSelector: '.clickCallback_left_0_tabRestoreDefaults'
				});
			};

			let sectionId, sectionIndividualTabStylesId;
			if (this.isPropertiesV2Enabled) {
				sectionId = 'tabs';
				sectionIndividualTabStylesId = 'tabs.individualTabStyles';
			} else {
				sectionId = 'general.tabs';
				sectionIndividualTabStylesId = 'general.tabs.individualTabStyles';
			}

			const model = this.getLayoutModel();
			const showDropDown = model.get('showDropDown');
			const disableShowTabs = !this._allowHidingMultipleTabs && model.items.length > 1;
			let propertyList = [{
				id: 'showTabsInPreview',
				supportsUndoRedo: true,
				getPropertyValue: () => !model.get('hideTab'),
				setPropertyValue: (value) => {
					const oldValue = !model.get('hideTab');
					if (!oldValue || oldValue !== value) {
						model.set({
							'hideTab': !value
						}, {
							payloadData: { skipUndoRedo: true }
						});
					}
				},
				editor: {
					sectionId: sectionId,
					uiControl: {
						type: 'ToggleButton',
						label: StringResources.get('propShowTabsInPreview'),
						readOnly: showDropDown || disableShowTabs,
						disabled: showDropDown || disableShowTabs
					}
				}
			}, {
				id: 'showDropDown',
				getPropertyValue: () => model.get('showDropDown'),
				setPropertyValue: (value, options) => {
					let data = {};
					if (options && options.transactionId) {
						data.undoRedoTransactionId = options.transactionId;
					}
					if (options && options.transactionToken) {
						data.transactionToken = options.transactionToken;
					}
					const oldOptions = {
						payloadData: data
					};
					const oldValue = model.get('showDropDown');
					if (!oldValue || oldValue !== value) {
						model.set({
							'showDropDown': value
						}, oldOptions);
					}
				},
				editor: {
					sectionId: sectionId,
					uiControl: {
						type: 'ToggleButton',
						label: 'Show dropdown',
						readOnly: false,
						disabled: false
					}
				}
			}];

			if (!showDropDown) {
				propertyList.push({
					id: 'tabPosition',
					supportsUndoRedo: true,
					getPropertyValue: () => model.get('tabPosition'),
					setPropertyValue: (value) => {
						const oldValue = model.get('tabPosition');
						if (!oldValue || oldValue !== value) {
							model.set({
								'tabPosition': value
							}, {
								payloadData: { skipUndoRedo: true }
							});
						}
					},
					editor: {
						sectionId: sectionId,
						uiControl: {
							type: 'DropDown',
							label: StringResources.get('propTabPosition'),
							options: TAB_POSITION
						}
					}
				}, {
					id: 'tabIconPosition',
					supportsUndoRedo: true,
					getPropertyValue: () => model.get('tabIconPosition'),
					setPropertyValue: (value) => {
						const oldValue = model.get('tabIconPosition');
						if (!oldValue || oldValue !== value) {
							model.set({
								'tabIconPosition': value
							}, {
								payloadData: { skipUndoRedo: true }
							});
						}
					},
					editor: {
						sectionId: sectionId,
						uiControl: {
							type: 'DropDown',
							label: StringResources.get('propTabIconPosition'),
							options: TAB_ICON_POSITION
						}
					}
				},
				this._generateColorPickerSpec('tabTextColor', `${sectionId}.masterTabStyles`, 'propTabTextColor'),
				this._generateColorPickerSpec('tabSelectedLineColor', `${sectionId}.masterTabStyles`, 'propTabSelectedBarColor'),
				this._generateColorPickerSpec('tabBackgroundColor', `${sectionId}.masterTabStyles`, 'propTabBackgroundColor'),
				{
					id: 'tabRestoreDefaults',
					editor: {
						sectionId: `${sectionId}.masterTabStyles`,
						uiControl: {
							type: 'SingleLineLinks',
							items: [{
								align: 'left',
								items: [{
									type: 'icon',
									class: 'iconTextLink',
									svgIcon: this._features['Dashboard.Icons'].getIcon('restore').id,
									iconTooltip: StringResources.get('propTabRestoreDefaults'),
									clickCallback: restoreMasterStyling
								}]
							}, {
								align: 'right',
								items: [{
									type: 'text',
									class: 'iconTextLink',
									value: StringResources.get('propTabRestoreDefaults'),
									ariaLabel: StringResources.get('propTabRestoreDefaults'),
									clickCallback: restoreMasterStyling
								}]
							}]
						}
					}
				}, {
					id: 'allowHidingMultipleTabs',
					getPropertyValue: () => this._allowHidingMultipleTabs,
					setPropertyValue: () => { throw new Error('not supported'); }
				});
				propertyList.push(...this._getIndividualTabStylesLinks(sectionIndividualTabStylesId));
			}
			const positionSectionPrefix = this.isPropertiesV2Enabled ? 'positionAndStyle' : 'general.layout.widgetPosition';
			const leftProperty = {
				id: 'left',
				getPropertyValue: () => {
					if (model.style) {
						return model.style.left;
					}
				},
				setPropertyValue: (value, options) => {
					this._updateStyleValueInModel('left', value, options);
				},
				editor: {
					sectionId: positionSectionPrefix + '.moveWidgetSplit.left',
					uiControl: {
						type: 'InputLabel',
						label: StringResources.get('propPositionXAxis_v1'),
						multiline: true,
						handleReturnKey: true,
						decimalPlaces: 2,
						onChange: this._onChangePercentOrPixelProperty.bind(this, 'width')
					}
				},
				onPropertyChange: {
					refresh: {
						propertiesPane: true
					}
				}
			};

			const topProperty = {
				id: 'top',
				getPropertyValue: () => {
					if (model.style) {
						return model.style.top;
					}
				},
				setPropertyValue: (value, options) => {
					this._updateStyleValueInModel('top', value, options);
				},
				editor: {
					sectionId: positionSectionPrefix + '.moveWidgetSplit.right',
					uiControl: {
						type: 'InputLabel',
						label: StringResources.get('propPositionYAxis_v1'),
						multiline: true,
						handleReturnKey: true,
						decimalPlaces: 2,
						onChange: this._onChangePercentOrPixelProperty.bind(this, 'height')
					}
				},
				onPropertyChange: {
					refresh: {
						propertiesPane: true
					}
				}
			};

			const resizeSectionPrefix = this.isPropertiesV2Enabled ? 'positionAndStyle' : 'general.layout.resizeWidget';
			const widthProperty = {
				id: 'width',
				getPropertyValue: () => {
					if (model.style) {
						return model.style.width;
					}
				},
				setPropertyValue: (value, options) => {
					this._updateStyleValueInModel('width', value, options);
				},
				editor: {
					sectionId: resizeSectionPrefix + '.resizeWidgetSplit.left',
					uiControl: {
						type: 'InputLabel',
						label: StringResources.get('propResizeWidth_v1'),
						multiline: true,
						handleReturnKey: true,
						decimalPlaces: 2,
						onChange: this._onChangePercentOrPixelProperty.bind(this, 'left')
					}
				},
				onPropertyChange: {
					refresh: {
						propertiesPane: true
					}
				}
			};

			const heightProperty = {
				id: 'height',
				getPropertyValue: () => {
					if (model.style) {
						return model.style.height;
					}
				},
				setPropertyValue: (value, options) => {
					this._updateStyleValueInModel('height', value, options);
				},
				editor: {
					sectionId: resizeSectionPrefix + '.resizeWidgetSplit.right',
					uiControl: {
						type: 'InputLabel',
						label: StringResources.get('propResizeHeight_v1'),
						multiline: true,
						handleReturnKey: true,
						decimalPlaces: 2,
						onChange: this._onChangePercentOrPixelProperty.bind(this, 'top')
					}
				},
				onPropertyChange: {
					refresh: {
						propertiesPane: true
					}
				}
			};

			propertyList.push(leftProperty, topProperty, widthProperty, heightProperty);
			return propertyList;
		}

		_getTabPropertiesList() {

			if (this._isInnerTab()) {
				return this._getInnerTabPropertiesList();
			}

			let sectionId, sectionIndividualTabStylesId;
			if (this.isPropertiesV2Enabled) {
				sectionId = 'tabs';
				sectionIndividualTabStylesId = 'tabs.individualTabStyles';
			} else {
				sectionId = 'general.tabs';
				sectionIndividualTabStylesId = 'general.tabs.individualTabStyles';
			}
			const sectionMasterTabStyles = sectionId + '.masterTabStyles';
			const restoreMasterStyling = () => {
				const transaction = this._features['Transaction'];
				const transactionToken = transaction.startTransaction();
				this._getCanvas().setPropertyValue('tabTextColor', 'transparent', transactionToken);
				this._getCanvas().setPropertyValue('tabSelectedLineColor', 'transparent', transactionToken);
				this._getCanvas().setPropertyValue('tabBackgroundColor', 'transparent', transactionToken);
				transaction.endTransaction(transactionToken);
				this._dashboard.triggerDashboardEvent('properties:refreshPane', {
					focusSelector: '.clickCallback_left_0_tabRestoreDefaults'
				});
			};

			const model = this.getLayoutModel();
			const disableShowTabs = !this._allowHidingMultipleTabs && model.items.length > 1;
			const propertyList = [{
				id: 'showTabsInPreview',
				supportsUndoRedo: true,
				getPropertyValue: () => !model.get('hideTab'),
				setPropertyValue: (value) => {
					const oldValue = !model.get('hideTab');
					if (!oldValue || oldValue !== value) {
						model.set({
							'hideTab': !value
						}, {
							payloadData: { skipUndoRedo: true }
						});
					}
				},
				editor: {
					sectionId: sectionId,
					uiControl: {
						type: 'ToggleButton',
						label: StringResources.get('propShowTabsInPreview'),
						readOnly: disableShowTabs,
						disabled: disableShowTabs
					}
				}
			}, {
				id: 'tabPosition',
				supportsUndoRedo: true,
				getPropertyValue: () => model.get('tabPosition'),
				setPropertyValue: (value) => {
					const oldValue = model.get('tabPosition');
					if (!oldValue || oldValue !== value) {
						model.set({
							'tabPosition': value
						}, {
							payloadData: { skipUndoRedo: true }
						});
					}
				},
				editor: {
					sectionId: sectionId,
					uiControl: {
						type: 'DropDown',
						label: StringResources.get('propTabPosition'),
						options: TAB_POSITION
					}
				}
			}, {
				id: 'tabIconPosition',
				supportsUndoRedo: true,
				getPropertyValue: () => model.get('tabIconPosition'),
				setPropertyValue: (value) => {
					const oldValue = model.get('tabIconPosition');
					if (!oldValue || oldValue !== value) {
						model.set({
							'tabIconPosition': value
						}, {
							payloadData: { skipUndoRedo: true }
						});
					}
				},
				editor: {
					sectionId: sectionId,
					uiControl: {
						type: 'DropDown',
						label: StringResources.get('propTabIconPosition'),
						options: TAB_ICON_POSITION
					}
				}
			},
			this._generateColorPickerSpec('tabTextColor', sectionMasterTabStyles, 'propTabTextColor'),
			this._generateColorPickerSpec('tabSelectedLineColor', sectionMasterTabStyles, 'propTabSelectedBarColor'),
			this._generateColorPickerSpec('tabBackgroundColor', sectionMasterTabStyles, 'propTabBackgroundColor'),
			{
				id: 'tabRestoreDefaults',
				editor: {
					sectionId: sectionMasterTabStyles,
					uiControl: {
						type: 'SingleLineLinks',
						items: [{
							align: 'left',
							items: [{
								type: 'icon',
								class: 'iconTextLink',
								svgIcon: this._features['Dashboard.Icons'].getIcon('restore').id,
								iconTooltip: StringResources.get('propTabRestoreDefaults'),
								clickCallback: restoreMasterStyling
							}]
						}, {
							align: 'right',
							items: [{
								type: 'text',
								class: 'iconTextLink',
								value: StringResources.get('propTabRestoreDefaults'),
								ariaLabel: StringResources.get('propTabRestoreDefaults'),
								clickCallback: restoreMasterStyling
							}]
						}]
					}
				}
			}, {
				id: 'allowHidingMultipleTabs',
				getPropertyValue: () => this._allowHidingMultipleTabs,
				setPropertyValue: () => { throw new Error('not supported'); }
			}];

			if (this.isPropertiesV2Enabled) {
				// Modify the property 'RestoreDefault' for master style links to align with Carbon design:
				// left: transparent icon (display nothing instead of the text 'Restore defaults'); right: icon restore and justifyContent='flex-end'
				const restoreProperty = propertyList.find(property => property.id === 'tabRestoreDefaults');
				restoreProperty.editor.uiControl.justifyContent = 'flex-end';
				restoreProperty.editor.uiControl.alignItems = 'center';
				const left = restoreProperty.editor.uiControl.items[0].items[0];
				left.type = 'text';
				left.grow = false;
				left.value = StringResources.get('propTabRestoreDefaults');
				left.ariaLabel = StringResources.get('propTabRestoreDefaults');
				delete left.iconTooltip;
				delete left.svgIcon;

				const right = restoreProperty.editor.uiControl.items[1].items[0];
				right.type = 'icon';
				right.svgIcon = this._features['Dashboard.Icons'].getIcon('restore').id;
				right.clickCallback = restoreMasterStyling;
				right.iconTooltip = StringResources.get('propTabRestoreDefaults');
				right.tooltipPosition = 'left';
				delete right.ariaLabel; // icon doesn't have these options
				delete right.value;

			}
			return propertyList.concat(this._getIndividualTabStylesLinks(sectionIndividualTabStylesId));
		}

		_changeTabColor(propertyName, propertyValue) {
			const payload = {};

			// Only get the colour class name if it is not transparent as we treat "transparent" as a reset to default colour
			if(propertyValue && propertyValue !== 'transparent') {
				payload[propertyName] = this._features['Colors'].getColorClassName(propertyValue);
			} else {
				payload[propertyName] = null;
			}

			this._features['Colors'].prepareForColorModelChange(payload, propertyName);

			this.getLayoutModel().set(payload, {
				sender: this.senderId,
				payloadData: { skipUndoRedo: true }
			});
		}

		_getPropertyValueFromModel(propName) {
			const model = this.getLayoutModel();
			// In case of livewidget previews, we have a content but it is not attached to the layout, which causes the model to be null.
			return model && model.get(propName);
		}

		_setPropertyValueInModel(propName, propValue) {
			const payload = {};
			const model = this.getLayoutModel();
			payload[propName] = propValue;
			model.set(payload, {
				payloadData: { skipUndoRedo: true }
			});
		}

		_generateColorPickerSpec(propertyId, sectionId, labelKey) {
			if (this.isPropertiesV2Enabled) {
				return {
					id: propertyId,
					supportsUndoRedo: true,
					getPropertyValue: () => this._getPropertyValueFromModel(propertyId) || 'transparent',
					setPropertyValue: (value) => this._changeTabColor(propertyId, value),
					editor: {
						sectionId,
						uiControl: {
							type: 'ColorPicker',
							label: StringResources.get(labelKey),
							paletteTitle: StringResources.get('themeLabel'),
							paletteType: 'DashboardColorSet',
							showHexValue: true,
							addButton: true,
						}
					}
				};
			} else {
				return {
					id: propertyId,
					supportsUndoRedo: true,
					setPropertyValue: (value, transactionToken) => {
						this._changeTabColor(propertyId, value, transactionToken);
					},
					editor: {
						sectionId,
						uiControl: {
							type: 'ColorPicker',
							label: StringResources.get(labelKey),
							ariaLabel: StringResources.get(labelKey),
							paletteType: 'DashboardColorSet',
							open: false,
							addButton: true,
							showHexValue: true,
							onChange: (propertyName, propertyValueInfo = {}) => {
								this._content.setPropertyValue(propertyName, propertyValueInfo.name);
							},
						}
					}
				};
			}
		}

	}

	return TabProperties;
});
